// src/utils/premiumService.js
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { app } from "../firebase";

const functions = getFunctions(app);

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/**
 * Asks the server for the user's tutor status.
 * Returns { status: "none" | "active" | "expired", daysLeft, premiumUntilMs, goal }
 */
export const fetchTutorStatus = async () => {
  const call = httpsCallable(functions, "getTutorStatus");
  const result = await call();
  return result.data;
};

/**
 * Opens Razorpay checkout for the ₹189 / 30-day premium pass.
 * onSuccess means checkout closed happily. It does NOT mean premium is on yet:
 * the server switches premium on when Razorpay's webhook arrives, so the
 * page waits for the status to change.
 */
export const processPremiumPayment = async (onSuccess, onFailure) => {
  let finished = false;
  const succeed = (details) => {
    if (finished) return;
    finished = true;
    if (onSuccess) onSuccess(details);
  };
  const fail = (error) => {
    if (finished) return;
    finished = true;
    if (onFailure) onFailure(error);
  };

  try {
    const loaded = await loadRazorpay();
    if (!loaded) {
      throw new Error("Couldn't load the payment window. Check your internet connection.");
    }

    const createPremiumOrder = httpsCallable(functions, "createPremiumOrder");
    const orderResult = await createPremiumOrder();
    if (!orderResult.data || !orderResult.data.success) {
      throw new Error("Couldn't create the payment order. Please try again.");
    }

    const { order, transactionId } = orderResult.data;
    const user = getAuth().currentUser;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      name: "TRESCEND",
      description: "Tutor premium, 30 days",
      order_id: order.id,
      handler: (response) =>
        succeed({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          transactionId,
        }),
      prefill: {
        name: user?.displayName || "",
        email: user?.email || "",
        contact: user?.phoneNumber || "",
      },
      notes: { purpose: "premium_pass", transaction_id: transactionId },
      theme: { color: "#f2cf4a" },
      modal: {
        // Small delay so a successful payment is never mistaken for a cancel
        ondismiss: () =>
          setTimeout(() => fail(new Error("Payment cancelled by user")), 400),
      },
    };

    const razorpay = new window.Razorpay(options);
    // A failed attempt keeps the checkout open so the user can retry inside it
    razorpay.on("payment.failed", (response) =>
      console.error("Payment attempt failed:", response.error)
    );
    razorpay.open();
  } catch (error) {
    console.error("Error in processPremiumPayment:", error);
    fail(error);
  }
};